import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import useTheme from 'hooks/useTheme';
import AntButton from 'components/AntButton/AntButton';
import { Checkbox, Modal, Tag, TextField, Wrapper } from 'components';

export const MAX_REASON_LENGTH = 500;
export const MIN_REASON_LENGTH = 10;

const FLAGS = [
	{
		key: 'requireInventoryToComplete',
		label: 'Material contabilizado (no serializado)',
		description:
			'Materiales sin número de serie registrados con cantidad positiva en el inventario de la orden.',
	},
	{
		key: 'requireSerializedToComplete',
		label: 'Equipo serializado',
		description:
			'Equipos identificados por serie registrados en el inventario de la orden.',
	},
	{
		key: 'requireNonEmptyInventoryToComplete',
		label: 'Material contabilizado o equipo serializado',
		description:
			'Al menos un material sin número de serie con cantidad positiva o un equipo identificado por serie registrado en el inventario de la orden.',
	},
];

export const getInventoryCompletionExceptionThemeTokens = theme => ({
	background: theme._primaryBackgroundColor,
	surface: theme._secondaryBackgroundColor,
	elevatedSurface: theme._surfaceBackgroundColor,
	textPrimary: theme._highEmphasisColor,
	textSecondary: theme._mediumEmphasisColor,
	textInactive: theme._inactiveColor,
	border: theme._borderColor,
	accent: theme._secondaryColor,
	warning: theme._warningColor,
});

const ModalContent = styled.div`
	--inventory-exception-background: ${({ $tokens }) => $tokens.background};
	--inventory-exception-surface: ${({ $tokens }) => $tokens.surface};
	--inventory-exception-surface-elevated: ${({ $tokens }) =>
		$tokens.elevatedSurface};
	--inventory-exception-text: ${({ $tokens }) => $tokens.textPrimary};
	--inventory-exception-text-secondary: ${({ $tokens }) =>
		$tokens.textSecondary};
	--inventory-exception-text-inactive: ${({ $tokens }) => $tokens.textInactive};
	--inventory-exception-border: ${({ $tokens }) => $tokens.border};
	--inventory-exception-accent: ${({ $tokens }) => $tokens.accent};
	--inventory-exception-warning: ${({ $tokens }) => $tokens.warning};

	background: var(--inventory-exception-background);
	box-sizing: border-box;
	color: var(--inventory-exception-text);
	display: flex;
	flex-direction: column;
	gap: 20px;
	max-height: min(72vh, 680px);
	overflow-y: auto;
	padding: 24px;

	@media (max-width: 560px) {
		gap: 16px;
		padding: 20px 16px;
	}
`;

const ContextCard = styled.section`
	background: var(--inventory-exception-surface);
	border: 1px solid var(--inventory-exception-border);
	border-left: 4px solid var(--inventory-exception-accent);
	border-radius: 8px;
	padding: 14px 16px;
`;

const SectionHeading = styled.h3`
	color: var(--inventory-exception-text);
	font-size: 15px;
	font-weight: 700;
	line-height: 1.4;
	margin: 0;
`;

const SupportingText = styled.p`
	color: var(--inventory-exception-text-secondary);
	font-size: 13px;
	line-height: 1.55;
	margin: 5px 0 0;
`;

const SingleRuleSection = styled.section`
	display: flex;
	flex-direction: column;
	gap: 10px;
`;

const RuleSummary = styled.div`
	align-items: center;
	background: var(--inventory-exception-surface-elevated);
	border: 1px solid var(--inventory-exception-accent);
	border-radius: 8px;
	display: flex;
	gap: 12px;
	min-height: 44px;
	padding: 13px 14px;
`;

const RuleMarker = styled.span`
	align-items: center;
	border: 1px solid var(--inventory-exception-accent);
	border-radius: 999px;
	color: var(--inventory-exception-accent);
	display: inline-flex;
	flex: 0 0 auto;
	font-size: 11px;
	font-weight: 700;
	justify-content: center;
	line-height: 1;
	min-height: 24px;
	padding: 0 9px;
	text-transform: uppercase;
`;

const RuleCopy = styled.span`
	display: flex;
	flex: 1 1 auto;
	flex-direction: column;
	gap: 3px;
	min-width: 0;
`;

const RuleName = styled.span`
	color: var(--inventory-exception-text);
	font-size: 14px;
	font-weight: 700;
	line-height: 1.4;
`;

const RuleDescription = styled.span`
	color: var(--inventory-exception-text-secondary);
	font-size: 12px;
	line-height: 1.45;
`;

const RuleFieldset = styled.fieldset`
	border: 0;
	margin: 0;
	min-width: 0;
	padding: 0;
`;

const RuleLegend = styled.legend`
	color: var(--inventory-exception-text);
	font-size: 15px;
	font-weight: 700;
	line-height: 1.4;
	margin: 0;
	padding: 0;
`;

const RuleOptions = styled.div`
	display: grid;
	gap: 10px;
	margin-top: 10px;
`;

const RuleOption = styled.div`
	background: ${({ $selected }) =>
		$selected
			? 'var(--inventory-exception-surface-elevated)'
			: 'var(--inventory-exception-surface)'};
	border: 1px solid
		${({ $selected }) =>
			$selected
				? 'var(--inventory-exception-accent)'
				: 'var(--inventory-exception-border)'};
	border-radius: 8px;
	opacity: ${({ $disabled }) => ($disabled ? 0.65 : 1)};
	transition:
		border-color 120ms ease,
		background-color 120ms ease;

	&:focus-within {
		outline: 2px solid var(--inventory-exception-accent);
		outline-offset: 2px;
	}

	.ant-checkbox-wrapper,
	> label {
		align-items: flex-start;
		box-sizing: border-box;
		cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
		display: flex;
		gap: 10px;
		margin: 0;
		min-height: 44px;
		padding: 13px 14px;
		width: 100%;
	}

	.ant-checkbox-wrapper-disabled {
		cursor: not-allowed;
	}

	.ant-checkbox-inner {
		background: var(--inventory-exception-background);
		border-color: var(--inventory-exception-border);
	}

	.ant-checkbox:hover .ant-checkbox-inner,
	.ant-checkbox-wrapper:hover .ant-checkbox-inner {
		border-color: var(--inventory-exception-accent);
	}

	.ant-checkbox-checked .ant-checkbox-inner {
		background: var(--inventory-exception-accent);
		border-color: var(--inventory-exception-accent);
	}

	.ant-checkbox-input:focus + .ant-checkbox-inner {
		border-color: var(--inventory-exception-accent);
		outline: 2px solid var(--inventory-exception-accent);
		outline-offset: 2px;
	}
`;

const ReasonSection = styled.section`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const ReasonHeader = styled.div`
	align-items: center;
	display: flex;
	gap: 10px;
	justify-content: space-between;
`;

const ReasonLabel = styled.label`
	color: var(--inventory-exception-text);
	font-size: 15px;
	font-weight: 700;
	line-height: 1.4;
`;

const RequiredBadge = styled.span`
	border: 1px solid var(--inventory-exception-border);
	border-radius: 999px;
	color: var(--inventory-exception-text-secondary);
	font-size: 11px;
	font-weight: 700;
	line-height: 1;
	padding: 5px 8px;
`;

const ReasonMeta = styled.div`
	align-items: flex-start;
	display: flex;
	gap: 12px;
	justify-content: space-between;
	min-height: 20px;
`;

const ReasonFeedback = styled.span`
	color: var(--inventory-exception-warning);
	font-size: 12px;
	line-height: 1.45;
`;

const CharacterCount = styled.span`
	color: ${({ $invalid }) =>
		$invalid
			? 'var(--inventory-exception-warning)'
			: 'var(--inventory-exception-text-secondary)'};
	flex: 0 0 auto;
	font-size: 12px;
	font-variant-numeric: tabular-nums;
	line-height: 1.45;
`;

const ActionFooter = styled.div`
	align-items: center;
	background: var(--inventory-exception-background);
	border-top: 1px solid var(--inventory-exception-border);
	display: flex;
	gap: 10px;
	justify-content: flex-end;
	margin: 0 -24px -24px;
	padding: 16px 24px 20px;
	position: sticky;
	bottom: -24px;

	@media (max-width: 560px) {
		margin: 0 -16px -20px;
		padding: 14px 16px 16px;

		button {
			min-height: 40px;
		}
	}
`;

const ModalActionButton = styled(AntButton)`
	&& {
		background: ${({ $intent }) =>
			$intent === 'primary'
				? 'var(--inventory-exception-accent)'
				: 'var(--inventory-exception-surface)'};
		border-color: ${({ $intent }) =>
			$intent === 'primary'
				? 'var(--inventory-exception-accent)'
				: 'var(--inventory-exception-border)'};
		border-radius: 8px;
		box-shadow: none;
		color: ${({ $intent }) =>
			$intent === 'primary'
				? 'var(--inventory-exception-background)'
				: 'var(--inventory-exception-text)'};
		font-weight: 700;
		min-height: 40px;
		min-width: 108px;
		padding: 0 18px;
		transition:
			background-color 120ms ease,
			border-color 120ms ease,
			box-shadow 120ms ease,
			color 120ms ease;
	}

	&&:hover,
	&&:focus {
		background: ${({ $intent }) =>
			$intent === 'primary'
				? 'var(--inventory-exception-accent)'
				: 'var(--inventory-exception-surface-elevated)'};
		border-color: var(--inventory-exception-accent);
		box-shadow: 0 0 0 2px var(--inventory-exception-border);
		color: ${({ $intent }) =>
			$intent === 'primary'
				? 'var(--inventory-exception-background)'
				: 'var(--inventory-exception-accent)'};
	}

	&&[disabled],
	&&[disabled]:hover,
	&&[disabled]:focus {
		background: var(--inventory-exception-surface-elevated);
		border-color: var(--inventory-exception-border);
		box-shadow: none;
		color: var(--inventory-exception-text-inactive);
		cursor: not-allowed;
		opacity: 1;
	}

	&&.ant-btn-loading,
	&&.ant-btn-loading[disabled] {
		background: var(--inventory-exception-accent);
		border-color: var(--inventory-exception-accent);
		color: var(--inventory-exception-background);
		opacity: 0.82;
	}

	&&.ant-btn-loading::before {
		background: var(--inventory-exception-surface);
		opacity: 0.12;
	}

	.anticon {
		color: currentColor;
	}
`;

const IdleState = styled.span`
	color: ${({ $tokens }) => $tokens.textSecondary};
	font-size: 12px;
	font-style: italic;
	line-height: 1.4;
`;

const LoadErrorState = styled.div`
	align-items: center;
	color: ${({ $tokens }) => $tokens.textSecondary};
	display: flex;
	flex-wrap: wrap;
	font-size: 12px;
	gap: 8px;
	line-height: 1.4;
`;

const getAvailableFlags = (management, mode) => {
	const restrictions = management?.restrictions || {};
	const activeOverrides = new Set(management?.activeOverrides || []);
	const staleOverrides = new Set(management?.staleOverrides || []);

	return FLAGS.filter(({ key }) => {
		const restriction = restrictions[key];
		if (!restriction) return false;
		if (mode === 'active') {
			return restriction.overridden === true && activeOverrides.has(key);
		}
		if (mode === 'stale') {
			return restriction.staleOverride === true && staleOverrides.has(key);
		}
		if (mode === 'revoke') {
			return (
				(restriction.overridden === true && activeOverrides.has(key)) ||
				(restriction.staleOverride === true && staleOverrides.has(key))
			);
		}
		return (
			restriction.templateRequired === true &&
			restriction.overridden === false &&
			restriction.staleOverride === false &&
			restriction.effectiveRequired === true
		);
	});
};

const createSelection = flags =>
	flags.reduce((selection, { key }) => ({ ...selection, [key]: false }), {});

const sameOrderId = (left, right) =>
	left !== undefined &&
	left !== null &&
	right !== undefined &&
	right !== null &&
	String(left) === String(right);

export default function InventoryCompletionExceptionControl({
	management,
	isLoading = false,
	loadError,
	onRetry,
	onSubmit,
	onError,
}) {
	const { theme, themeProfile } = useTheme();
	const themeTokens = getInventoryCompletionExceptionThemeTokens(theme);
	const [mode, setMode] = useState();
	const [reason, setReason] = useState('');
	const [selected, setSelected] = useState({});
	const [submitting, setSubmitting] = useState(false);
	const submittingRef = useRef(false);
	const lifecycleRef = useRef(0);
	const modeRef = useRef(mode);
	const selectedRef = useRef(selected);
	const managementOrderIdRef = useRef(management?.orderId);
	modeRef.current = mode;
	selectedRef.current = selected;

	const grantFlags = getAvailableFlags(management, 'grant');
	const revokeFlags = getAvailableFlags(management, 'revoke');
	const activeFlags = getAvailableFlags(management, 'active');
	const staleFlags = getAvailableFlags(management, 'stale');
	const modalFlags = mode === 'grant' ? grantFlags : revokeFlags;
	const availableFlagKeys = new Set(modalFlags.map(({ key }) => key));
	const normalizedReason = reason.trim();
	const selectedFlags =
		modalFlags.length === 1
			? [modalFlags[0].key]
			: Object.keys(selected).filter(
					key => selected[key] && availableFlagKeys.has(key),
				);
	const reasonTooShort =
		normalizedReason.length > 0 && normalizedReason.length < MIN_REASON_LENGTH;
	const reasonTooLong = normalizedReason.length > MAX_REASON_LENGTH;
	const isUnavailable = isLoading || Boolean(loadError);
	const interactionDisabled = submitting || isUnavailable;
	const closeDisabled = submitting || isLoading;
	const canAct =
		!isUnavailable &&
		management?.canManage === true &&
		management?.orderOpen === true;
	const hasVisibleStatus = activeFlags.length > 0 || staleFlags.length > 0;
	const hasVisibleAction =
		canAct && (grantFlags.length > 0 || revokeFlags.length > 0);
	const invalid =
		!canAct ||
		!normalizedReason ||
		reasonTooShort ||
		reasonTooLong ||
		selectedFlags.length === 0;
	const modalTitle =
		mode === 'grant'
			? 'Permitir completar sin materiales'
			: 'Restablecer restricción de inventario';
	const reasonLabel =
		mode === 'grant'
			? 'Razón de la autorización'
			: 'Razón para restablecer la restricción';
	const reasonErrorId = reasonTooShort
		? 'inventory-completion-reason-too-short'
		: reasonTooLong
			? 'inventory-completion-reason-too-long'
			: undefined;
	const reasonHelpId = 'inventory-completion-reason-help';
	const reasonDescribedBy = [reasonHelpId, reasonErrorId]
		.filter(Boolean)
		.join(' ');
	const selectionHeading =
		mode === 'grant'
			? 'Esta autorización aplicará a'
			: 'Se restablecerá la validación de';
	const contextHeading =
		mode === 'grant'
			? 'Excepción administrativa de un solo uso'
			: 'Restablecer la validación antes del cierre';
	const contextDescription =
		mode === 'grant'
			? 'La orden podrá completarse sin cumplir las reglas autorizadas. La excepción se consumirá únicamente después de un cierre exitoso.'
			: 'La orden volverá a exigir las reglas seleccionadas antes de que un técnico pueda completarla.';

	useEffect(() => {
		const currentMode = modeRef.current;
		const currentSelected = selectedRef.current;
		const previousOrderId = managementOrderIdRef.current;
		const nextOrderId = management?.orderId;
		managementOrderIdRef.current = nextOrderId;
		lifecycleRef.current += 1;
		submittingRef.current = false;
		setSubmitting(false);

		if (currentMode) {
			const nextFlags = getAvailableFlags(management, currentMode);
			const nextFlagKeys = new Set(nextFlags.map(({ key }) => key));
			const selectedFlagChanged = Object.keys(currentSelected).some(
				key => currentSelected[key] === true && !nextFlagKeys.has(key),
			);
			const implicitSingleFlagChanged =
				Object.keys(currentSelected).length === 1 &&
				!nextFlagKeys.has(Object.keys(currentSelected)[0]);
			const closeForFreshState =
				!sameOrderId(previousOrderId, nextOrderId) ||
				management?.canManage !== true ||
				management?.orderOpen !== true ||
				nextFlags.length === 0 ||
				selectedFlagChanged ||
				implicitSingleFlagChanged;

			if (closeForFreshState) {
				setMode(undefined);
				setReason('');
				setSelected({});
			} else {
				setSelected(
					nextFlags.reduce(
						(nextSelected, { key }) => ({
							...nextSelected,
							[key]: currentSelected[key] === true,
						}),
						{},
					),
				);
			}
		}
		return () => {
			lifecycleRef.current += 1;
		};
	}, [management]);

	const closeModal = () => {
		if (submittingRef.current || isLoading) return;
		setMode(undefined);
		setReason('');
		setSelected({});
	};

	const openModal = nextMode => {
		const availableFlags = nextMode === 'grant' ? grantFlags : revokeFlags;
		setMode(nextMode);
		setReason('');
		setSelected(createSelection(availableFlags));
	};

	const handleSubmit = async () => {
		const currentFlags = mode === 'grant' ? grantFlags : revokeFlags;
		const currentFlagKeys = new Set(currentFlags.map(({ key }) => key));
		const validatedSelectedFlags =
			currentFlags.length === 1
				? [currentFlags[0].key]
				: Object.keys(selected).filter(
						key => selected[key] && currentFlagKeys.has(key),
					);
		if (
			invalid ||
			!canAct ||
			validatedSelectedFlags.length === 0 ||
			submittingRef.current ||
			!mode
		)
			return;
		const lifecycle = lifecycleRef.current;
		submittingRef.current = true;
		setSubmitting(true);
		const overrideValue = mode === 'grant' ? false : null;
		const overrides = validatedSelectedFlags.reduce(
			(result, flag) => ({ ...result, [flag]: overrideValue }),
			{},
		);

		try {
			await onSubmit({
				reason: normalizedReason,
				overrides,
			});
			if (lifecycle !== lifecycleRef.current) return;
			submittingRef.current = false;
			setSubmitting(false);
			setMode(undefined);
			setReason('');
			setSelected({});
		} catch (error) {
			if (lifecycle !== lifecycleRef.current) return;
			submittingRef.current = false;
			setSubmitting(false);
			if (onError) onError(error);
		}
	};

	return (
		<Wrapper
			padding='0'
			alignItems='center'
			justifyContent='flex-end'
			flexWrap='wrap'
			aria-busy={isLoading}
			data-testid='inventory-completion-exception-control'
		>
			{isLoading && (
				<IdleState $tokens={themeTokens} aria-live='polite'>
					Consultando reglas…
				</IdleState>
			)}
			{!isLoading && loadError && !mode && (
				<LoadErrorState $tokens={themeTokens} role='alert'>
					<span>{loadError}</span>
					<AntButton
						size='small'
						aria-label='Reintentar consulta de reglas de cierre'
						disabled={!onRetry}
						onClick={onRetry}
					>
						Reintentar
					</AntButton>
				</LoadErrorState>
			)}
			{!isUnavailable &&
				management &&
				!hasVisibleStatus &&
				!hasVisibleAction && (
					<IdleState $tokens={themeTokens}>Sin acciones disponibles</IdleState>
				)}
			{!isUnavailable &&
				activeFlags.map(({ key, label }) => (
					<Wrapper key={key} padding='0 8px 0 0' width='auto'>
						<Tag>Excepción activa: {label}</Tag>
					</Wrapper>
				))}
			{!isUnavailable &&
				staleFlags.map(({ key, label }) => (
					<Wrapper key={key} padding='0 8px 0 0' width='auto'>
						<Tag>Configuración obsoleta: {label}</Tag>
					</Wrapper>
				))}

			{canAct && grantFlags.length > 0 && (
				<AntButton
					type='primary'
					size='small'
					onClick={() => openModal('grant')}
				>
					Permitir completar sin materiales
				</AntButton>
			)}
			{canAct && revokeFlags.length > 0 && (
				<AntButton size='small' onClick={() => openModal('revoke')}>
					Restablecer restricción
				</AntButton>
			)}

			<Modal
				visible={!!mode}
				title={modalTitle}
				aria-label={modalTitle}
				footer={null}
				width={600}
				destroyOnClose
				onCancel={closeModal}
				closable={!closeDisabled}
				maskClosable={!closeDisabled}
			>
				<ModalContent
					data-testid='inventory-completion-exception-modal'
					data-theme={themeProfile}
					aria-busy={isLoading}
					$tokens={themeTokens}
				>
					<ContextCard aria-labelledby='inventory-completion-context-title'>
						<SectionHeading id='inventory-completion-context-title'>
							{contextHeading}
						</SectionHeading>
						<SupportingText>{contextDescription}</SupportingText>
					</ContextCard>

					{loadError && (
						<LoadErrorState $tokens={themeTokens} role='alert'>
							<span>{loadError}</span>
							<AntButton
								size='small'
								aria-label='Reintentar consulta de reglas de cierre'
								disabled={!onRetry}
								onClick={onRetry}
							>
								Reintentar
							</AntButton>
						</LoadErrorState>
					)}

					{modalFlags.length === 1 ? (
						<SingleRuleSection aria-labelledby='inventory-completion-rule-summary-title'>
							<SectionHeading id='inventory-completion-rule-summary-title'>
								{selectionHeading}
							</SectionHeading>
							<RuleSummary role='status'>
								<RuleMarker>1 regla</RuleMarker>
								<RuleCopy>
									<RuleName>{modalFlags[0].label}</RuleName>
									<RuleDescription>{modalFlags[0].description}</RuleDescription>
								</RuleCopy>
							</RuleSummary>
						</SingleRuleSection>
					) : (
						<RuleFieldset disabled={interactionDisabled}>
							<RuleLegend>
								Seleccione las reglas que desea{' '}
								{mode === 'grant' ? 'autorizar' : 'restablecer'}
							</RuleLegend>
							<SupportingText>
								Seleccione al menos una. Puede aplicar la acción a una o varias
								reglas para esta orden.
							</SupportingText>
							<RuleOptions>
								{modalFlags.map(({ key, label, description }) => (
									<RuleOption
										key={key}
										$selected={selected[key] === true}
										$disabled={interactionDisabled}
									>
										<Checkbox
											name={key}
											checked={selected[key] === true}
											disabled={interactionDisabled}
											onChange={({ target }) =>
												setSelected(current => ({
													...current,
													[key]: target.checked,
												}))
											}
										>
											<RuleCopy>
												<RuleName>{label}</RuleName>
												<RuleDescription>{description}</RuleDescription>
											</RuleCopy>
										</Checkbox>
									</RuleOption>
								))}
							</RuleOptions>
						</RuleFieldset>
					)}

					<ReasonSection aria-labelledby='inventory-completion-reason-title'>
						<ReasonHeader>
							<ReasonLabel
								id='inventory-completion-reason-title'
								htmlFor='inventory-completion-reason'
							>
								{reasonLabel}
							</ReasonLabel>
							<RequiredBadge>Obligatorio</RequiredBadge>
						</ReasonHeader>
						<SupportingText id={reasonHelpId}>
							Explique por qué esta orden requiere la acción. Use entre 10 y 500
							caracteres.
						</SupportingText>
						<TextField.TextArea
							id='inventory-completion-reason'
							value={reason}
							required
							disabled={interactionDisabled}
							onChange={({ target }) => setReason(target.value)}
							rows={4}
							aria-label={reasonLabel}
							aria-required='true'
							aria-describedby={reasonDescribedBy}
							aria-invalid={reasonTooShort || reasonTooLong || undefined}
							placeholder='Describa el motivo para que quede registrado en el historial de la orden'
						/>
						<ReasonMeta>
							<span>
								{reasonTooShort && (
									<ReasonFeedback
										id={reasonErrorId}
										role='alert'
										aria-live='polite'
									>
										La razón tiene un mínimo de 10 caracteres.
									</ReasonFeedback>
								)}
								{reasonTooLong && (
									<ReasonFeedback
										id={reasonErrorId}
										role='alert'
										aria-live='polite'
									>
										La razón tiene un máximo de 500 caracteres.
									</ReasonFeedback>
								)}
							</span>
							<CharacterCount $invalid={reasonTooLong} aria-live='polite'>
								{normalizedReason.length}/{MAX_REASON_LENGTH}
							</CharacterCount>
						</ReasonMeta>
					</ReasonSection>

					<ActionFooter>
						<ModalActionButton
							$intent='secondary'
							data-intent='secondary'
							disabled={closeDisabled}
							onClick={closeModal}
						>
							Cancelar
						</ModalActionButton>
						<ModalActionButton
							$intent='primary'
							type='primary'
							data-intent='primary'
							data-action='confirm'
							disabled={invalid || submitting}
							loading={submitting}
							onClick={handleSubmit}
						>
							Confirmar
						</ModalActionButton>
					</ActionFooter>
				</ModalContent>
			</Modal>
		</Wrapper>
	);
}
